package ai.jessie.web.documents

import ai.jessie.shared.documents.DocumentChoiceResponse
import ai.jessie.shared.documents.DocumentDetailResponse
import ai.jessie.shared.documents.DocumentDetailsDto
import ai.jessie.web.theme.ButtonVariant
import ai.jessie.web.theme.CheckHintStyle
import ai.jessie.web.theme.FieldErrorStyle
import ai.jessie.web.theme.FieldInputStyle
import ai.jessie.web.theme.FieldStyle
import ai.jessie.web.theme.FieldsetStyle
import ai.jessie.web.theme.Grid2Style
import ai.jessie.web.theme.LabelStyle
import ai.jessie.web.theme.NumInputStyle
import ai.jessie.web.theme.RowStyle
import ai.jessie.web.theme.StackStyle
import ai.jessie.web.theme.TextAreaStyle
import ai.jessie.web.theme.themedButton
import androidx.compose.runtime.Composable
import com.varabyte.kobweb.silk.style.toAttrs
import org.jetbrains.compose.web.attributes.AttrsScope
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.forId
import org.jetbrains.compose.web.attributes.selected
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Fieldset
import org.jetbrains.compose.web.dom.Form
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Legend
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.TextArea

// Steps 4-6 and B7: every field is shown; only the server's editable fields accept input. Enter
// saves; a transition carries unsaved edits only once confirmed.
@Composable
internal fun detailsForm(state: DocumentWorkflowUiState, actions: DocumentWorkflowActions) {
    val detail = state.detail ?: return
    val draft = state.draft ?: return
    val locked = state.busy || state.confirmation != null
    val field = FieldContext(detail.editableFields.toSet(), state.problems)
    Form(
        attrs = {
            attr("aria-label", "Angaben des Dokuments")
            addEventListener("submit") {
                it.preventDefault()
                actions.save()
            }
        }
    ) {
        Fieldset(attrs = FieldsetStyle.toAttrs { if (locked) attr("disabled", "") }) {
            Legend(attrs = LabelStyle.toAttrs()) { Text("Klassifizierung") }
            Div(attrs = StackStyle.toAttrs()) { classification(detail, draft, field, actions) }
        }
        Fieldset(attrs = FieldsetStyle.toAttrs { if (locked) attr("disabled", "") }) {
            Legend(attrs = LabelStyle.toAttrs()) { Text("Angaben") }
            Div(attrs = StackStyle.toAttrs()) { details(draft, field, actions) }
        }
        if (SAVE_DETAILS in detail.actions)
            Div(attrs = RowStyle.toAttrs()) {
                themedButton(
                    "Angaben speichern",
                    {},
                    variant = ButtonVariant.PRIMARY,
                    disabled = locked || !state.dirty,
                    submit = true,
                )
                if (state.dirty)
                    themedButton("Änderungen verwerfen", actions.discard, disabled = locked)
            }
    }
}

// Step 4, A3, BR-130, BR-132: active same-organisation choices only; an accountant keeps the
// existing classification.
@Composable
private fun classification(
    detail: DocumentDetailResponse,
    draft: DocumentDetailsDto,
    field: FieldContext,
    actions: DocumentWorkflowActions,
) {
    Div(attrs = Grid2Style.toAttrs()) {
        choiceField(
            "categoryId",
            "Kategorie (für die Archivierung erforderlich)",
            detail.category,
            detail.categories,
            draft.categoryId,
            field,
        ) { value ->
            actions.edit { it.copy(categoryId = value.ifEmpty { null }) }
        }
        choiceField(
            "folderId",
            "Ordner (optional)",
            detail.folder,
            detail.folders,
            draft.folderId,
            field,
        ) { value ->
            actions.edit { it.copy(folderId = value.ifEmpty { null }) }
        }
    }
    val id = documentInputId("accountingRelevant")
    Div(attrs = FieldStyle.toAttrs()) {
        Div(attrs = RowStyle.toAttrs()) {
            Input(
                InputType.Checkbox,
                attrs = {
                    id(id)
                    attr("name", "accountingRelevant")
                    checked(draft.accountingRelevant)
                    if ("accountingRelevant" !in field.editable) attr("disabled", "")
                    problemAttributes(field.problems["accountingRelevant"], id)
                    onChange { change ->
                        actions.edit { it.copy(accountingRelevant = change.value) }
                    }
                },
            )
            Label(attrs = { forId(id) }) { Text("Buchhaltungsrelevant") }
        }
        P(attrs = CheckHintStyle.toAttrs()) {
            Text(
                "Buchhaltungsrelevante Dokumente sehen auch Buchhalter. Sie werden über die " +
                    "Buchhaltung archiviert und brauchen dafür Dokumentdatum, Währung, Netto- und " +
                    "Bruttobetrag."
            )
        }
        fieldProblem(field.problems["accountingRelevant"], id)
    }
}

// Step 5 with the V017 limits; amounts stay exact decimal text (CAP-006).
@Composable
private fun details(
    draft: DocumentDetailsDto,
    field: FieldContext,
    actions: DocumentWorkflowActions,
) {
    textField("title", "Titel (Pflichtfeld)", draft.title, field, max = MAX_TITLE) { value ->
        actions.edit { it.copy(title = value) }
    }
    Div(attrs = Grid2Style.toAttrs()) {
        DETAIL_FIELDS.forEach { spec ->
            textField(
                spec.name,
                spec.label,
                spec.value(draft),
                field,
                spec.type,
                spec.max,
                spec.decimal,
            ) { value ->
                actions.edit { spec.update(it, value.ifEmpty { null }) }
            }
        }
    }
    val id = documentInputId("summary")
    Div(attrs = FieldStyle.toAttrs()) {
        Label(attrs = { forId(id) }) { Text("Zusammenfassung (optional)") }
        TextArea(
            value = draft.summary.orEmpty(),
            attrs =
                TextAreaStyle.toAttrs {
                    id(id)
                    attr("name", "summary")
                    attr("rows", "3")
                    attr("maxlength", "$MAX_SUMMARY")
                    if ("summary" !in field.editable) attr("readonly", "")
                    problemAttributes(field.problems["summary"], id)
                    onInput { change ->
                        actions.edit { it.copy(summary = change.value.ifEmpty { null }) }
                    }
                },
        )
        fieldProblem(field.problems["summary"], id)
    }
}

// One optional detail input; `update` receives the entered text, absent when empty.
private class DetailField(
    val name: String,
    val label: String,
    val value: (DocumentDetailsDto) -> String?,
    val update: DocumentDetailsDto.(String?) -> DocumentDetailsDto,
    val type: InputType<String> = InputType.Text,
    val max: Int? = null,
    val decimal: Boolean = false,
)

private val DETAIL_FIELDS =
    listOf(
        DetailField(
            "documentType",
            "Dokumentart (optional)",
            { it.documentType },
            { copy(documentType = it) },
            max = MAX_DOCUMENT_TYPE,
        ),
        DetailField(
            "externalInvoiceId",
            "Externe Rechnungsnummer (optional)",
            { it.externalInvoiceId },
            { copy(externalInvoiceId = it) },
            max = MAX_EXTERNAL_ID,
        ),
        DetailField(
            "issuerName",
            "Aussteller (optional)",
            { it.issuerName },
            { copy(issuerName = it) },
            max = MAX_NAME,
        ),
        DetailField(
            "recipientName",
            "Empfänger (optional)",
            { it.recipientName },
            { copy(recipientName = it) },
            max = MAX_NAME,
        ),
        DetailField(
            "documentDate",
            "Dokumentdatum (optional)",
            { it.documentDate },
            { copy(documentDate = it) },
            InputType.Date,
        ),
        DetailField(
            "dueDate",
            "Fälligkeitsdatum (optional)",
            { it.dueDate },
            { copy(dueDate = it) },
            InputType.Date,
        ),
        // B7: upper-case ISO 4217; the server checks the code.
        DetailField(
            "currency",
            "Währung (ISO-Code, z. B. CHF)",
            { it.currency },
            { copy(currency = it?.uppercase()) },
            max = CURRENCY_LENGTH,
        ),
        DetailField(
            "totalNet",
            "Nettobetrag (optional)",
            { it.totalNet },
            { copy(totalNet = it) },
            decimal = true,
        ),
        DetailField(
            "totalGross",
            "Bruttobetrag (nicht kleiner als netto)",
            { it.totalGross },
            { copy(totalGross = it) },
            decimal = true,
        ),
    )

private class FieldContext(val editable: Set<String>, val problems: Map<String, String>)

@Composable
private fun textField(
    name: String,
    label: String,
    value: String?,
    field: FieldContext,
    type: InputType<String> = InputType.Text,
    max: Int? = null,
    decimal: Boolean = false,
    change: (String) -> Unit,
) {
    val id = documentInputId(name)
    val problem = field.problems[name]
    Div(attrs = FieldStyle.toAttrs()) {
        Label(attrs = { forId(id) }) { Text(label) }
        Input(
            type,
            attrs =
                (if (decimal) NumInputStyle else FieldInputStyle).toAttrs {
                    id(id)
                    attr("name", name)
                    attr("autocomplete", "off")
                    value(value.orEmpty())
                    max?.let { attr("maxlength", "$it") }
                    if (decimal) attr("inputmode", "decimal")
                    if (name !in field.editable) attr("readonly", "")
                    problemAttributes(problem, id)
                    onInput { change(it.value) }
                },
        )
        fieldProblem(problem, id)
    }
}

// A deactivated current choice stays shown and selectable until it is replaced (BR-132).
@Composable
private fun choiceField(
    name: String,
    label: String,
    current: DocumentChoiceResponse?,
    choices: List<DocumentChoiceResponse>,
    value: String?,
    field: FieldContext,
    change: (String) -> Unit,
) {
    val id = documentInputId(name)
    val problem = field.problems[name]
    val options =
        choices + listOfNotNull(current?.takeIf { shown -> choices.none { it.id == shown.id } })
    Div(attrs = FieldStyle.toAttrs()) {
        Label(attrs = { forId(id) }) { Text(label) }
        if (name !in field.editable)
            Input(
                InputType.Text,
                attrs =
                    FieldInputStyle.toAttrs {
                        id(id)
                        attr("readonly", "")
                        value(current?.let(::choiceLabel) ?: "Keine Auswahl")
                    },
            )
        else
            Select(
                attrs =
                    FieldInputStyle.toAttrs {
                        id(id)
                        attr("name", name)
                        problemAttributes(problem, id)
                        onChange { change(it.value.orEmpty()) }
                    }
            ) {
                Option("", attrs = { if (value == null) selected() }) { Text("Keine Auswahl") }
                options.forEach { choice ->
                    Option(
                        choice.id,
                        attrs = { if (choice.id.equals(value, ignoreCase = true)) selected() },
                    ) {
                        Text(choiceLabel(choice))
                    }
                }
            }
        fieldProblem(problem, id)
    }
}

private fun choiceLabel(choice: DocumentChoiceResponse) =
    if (choice.active) choice.name else "${choice.name} (inaktiv)"

internal fun AttrsScope<*>.problemAttributes(problem: String?, id: String) {
    if (problem == null) return
    attr("aria-invalid", "true")
    attr("aria-describedby", "$id-problem")
}

@Composable
internal fun fieldProblem(problem: String?, id: String) {
    problem?.let { P(attrs = FieldErrorStyle.toAttrs { id("$id-problem") }) { Text(it) } }
}

private const val MAX_TITLE = 200
private const val MAX_NAME = 200
private const val MAX_SUMMARY = 2000
private const val MAX_DOCUMENT_TYPE = 50
private const val MAX_EXTERNAL_ID = 100
private const val CURRENCY_LENGTH = 3
